import React, { memo, useState } from 'react';
import {
  LoadingContainer,
  Space,
  PanelCompatibleContainer,
  Text,
} from '@redlink/components/atoms';
import { View, TouchableOpacity, ScrollView } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import { useLocale } from '@redlink/assets/locale';
import { useValues } from '@redlink/assets/values';
import { SetupPageTitle, Button } from '@redlink/components/molecules';
import { useQuery, getTagsQuery } from '@redlink/services';
import { isMobile } from 'react-device-detect';
import { useMutation, editPageTagsQuery } from '@redlink/services';
import { useNavigation, StackActions } from '@react-navigation/native';
import { freshCache } from './cache';

export const PageTagsSetupPage = memo(({ navigation }) => {
  const { styles } = useStyle();
  const { dictionary } = useLocale();
  const { routes } = useValues();
  const [selectedTags, setSelectedTags] = useState([]);
  const { dispatch: navigationDispatch } = useNavigation();

  const [edit, { loading }] = useMutation(editPageTagsQuery, {
    done: () => navigationDispatch(StackActions.replace(routes.dashboard.key)),
    update: freshCache,
  });

  const {
    loading: getTagsIsBusy,
    data: { Tags = [] },
  } = useQuery(getTagsQuery);

  return (
    <LoadingContainer
      isBusy={getTagsIsBusy || loading}
      style={styles.container}
    >
      <PanelCompatibleContainer>
        <SetupPageTitle
          title={dictionary.setupPageTitle}
          subtitle={dictionary.setupPageTagsTitle}
          description={dictionary.setupPageTagsDescription}
          step={4}
        />
        <Space />
        <TagSelect
          tags={Tags}
          selectedTags={selectedTags}
          onChange={(items) => setSelectedTags(items)}
        />
        <Space />
        <Button
          style={styles.formActionContainer}
          title={dictionary.startPage}
          variant="primary"
          onPress={() => {
            edit({
              tgas: selectedTags,
            });
          }}
        />
        <Space />
      </PanelCompatibleContainer>
    </LoadingContainer>
  );
});

const TagSelect = memo(({ tags, selectedTags, onChange }) => {
  const { styles } = useStyle();
  return (
    <ScrollView>
      <View style={styles.tagsContainer}>
        {tags.map(({ title }, index) => {
          const selected = selectedTags.includes(title);
          return (
            <TouchableOpacity
              activeOpacity={0.7}
              style={[
                styles.tagContainer,
                selected && styles.tagContainerSelected,
              ]}
              onPress={() =>
                onChange(
                  !selected
                    ? [...selectedTags, title]
                    : selectedTags.filter((p) => p !== title)
                )
              }
            >
              <Text
                style={[styles.tagText, selected && styles.tagTextSelected]}
                numberOfLines={1}
              >
                {title}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </ScrollView>
  );
});

const useStyle = createUseStyle(({ colors, size }) => ({
  container: {
    flex: 1,
    backgroundColor: colors.white,
    paddingHorizontal: isMobile ? size.mobilePagePadding : size.pagePadding,
    paddingVertical: isMobile
      ? size.mobilePagePadding
      : size.verticalPagePadding,
  },
  formActionContainer: {
    minHeight: 50,
  },
  tagsContainer: {
    flex: 1,
    flexDirection: 'row',
    gap: 5,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  tagContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 30,
    borderWidth: 1,
    borderColor: colors.blue8,
    backgroundColor: colors.blue8,
  },
  tagContainerSelected: {
    borderColor: colors.blue1,
    backgroundColor: colors.blue1,
  },
  tagText: {
    color: colors.blue1,
  },
  tagTextSelected: {
    color: colors.white,
  },
}));
